import { useMemo, useState } from 'react'
import { getSortedTimezones } from '../utils/timezone'
import { useUserService } from '@abroad/components'

/**
 * Custom hook for managing session timezone
 * Handles timezone detection and validation
 */
export const useSessionTimezone = () => {
  const { user } = useUserService()

  // Get default timezone: user timezone > browser timezone > fallback
  const getDefaultTimezone = useMemo(() => {
    // First, try to get from user object
    const userTimezone = user?.timezone

    // Second, try to detect browser timezone
    let browserTimezone = ''
    try {
      if (
        typeof Intl !== 'undefined' &&
        Intl.DateTimeFormat &&
        Intl.DateTimeFormat().resolvedOptions
      ) {
        browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone || ''
      }
    } catch (e) {
      browserTimezone = ''
    }

    // Try user timezone first, then browser timezone
    const detectedTimezone = userTimezone || browserTimezone

    // Validate that the timezone exists in the timezone list
    if (detectedTimezone) {
      const timezones = getSortedTimezones()
      const timezoneExists = timezones.some(
        (tz) => tz.value === detectedTimezone,
      )
      if (timezoneExists) {
        return detectedTimezone
      }
    }

    // Fallback to 'Asia/Kolkata' if detection fails or timezone not in list
    return 'Asia/Kolkata'
  }, [user])

  const [clientTimezone, setClientTimezone] = useState(getDefaultTimezone)

  const handleTimezoneChange = (e) => {
    setClientTimezone(e.target.value)
  }

  return {
    clientTimezone,
    setClientTimezone,
    handleTimezoneChange,
    defaultTimezone: getDefaultTimezone,
  }
}
